/**
 * Request batcher for market fetching
 *
 * Collects individual market ID requests within a short time window
 * and batches them into a single getMarketsByIds call for efficiency.
 *
 * This prevents the "100 separate calls for 1 market each" anti-pattern.
 */

import type { Market } from '@/common/predictionMarkets/types';

type BatchResolver = {
  marketId: string;
  resolve: (market: Market | undefined) => void;
  reject: (error: Error) => void;
};

export class MarketBatcher {
  private batchQueue: BatchResolver[] = [];
  private batchTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly batchWindowMs: number;
  private readonly fetchFn: (marketIds: string[]) => Promise<Record<string, Market>>;

  constructor(
    fetchFn: (marketIds: string[]) => Promise<Record<string, Market>>,
    batchWindowMs: number = 10, // 10ms default - short enough to be imperceptible, long enough to batch
  ) {
    this.fetchFn = fetchFn;
    this.batchWindowMs = batchWindowMs;
  }

  /**
   * Request a single market. Will be batched automatically.
   */
  async load(marketId: string): Promise<Market | undefined> {
    return new Promise((resolve, reject) => {
      this.batchQueue.push({ marketId, resolve, reject });

      // If this is the first request in the batch, start the timer
      if (this.batchTimer === null) {
        this.batchTimer = setTimeout(() => {
          this.flush();
        }, this.batchWindowMs);
      }
    });
  }

  /**
   * Request multiple markets. Will be batched with other requests.
   */
  async loadMany(marketIds: string[]): Promise<Record<string, Market>> {
    const promises = marketIds.map((id) => this.load(id));
    const markets = await Promise.all(promises);

    const result: Record<string, Market> = {};
    marketIds.forEach((marketId, i) => {
      const market = markets[i];
      if (market) {
        result[marketId] = market;
      }
    });
    return result;
  }

  /**
   * Flush all pending requests immediately
   */
  private async flush() {
    if (this.batchTimer !== null) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }

    if (this.batchQueue.length === 0) {
      return;
    }

    // Take current queue and reset
    const batch = this.batchQueue.splice(0);

    // Extract unique market IDs
    const uniqueMarketIds = Array.from(new Set(batch.map((r) => r.marketId)));

    try {
      // Fetch all markets in one batch
      const markets = await this.fetchFn(uniqueMarketIds);

      // Resolve each promise with its corresponding market
      for (const resolver of batch) {
        resolver.resolve(markets[resolver.marketId]);
      }
    } catch (error) {
      // Reject all promises in this batch
      for (const resolver of batch) {
        resolver.reject(error as Error);
      }
    }
  }

  /**
   * Clear any pending batches (useful for cleanup/testing)
   */
  clear() {
    if (this.batchTimer !== null) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }
    this.batchQueue = [];
  }
}
